import React, { useState, useEffect } from "react";
import axios from "../utils/axiosConfig";
import {
  Box, Button, TextField, Typography
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

import { handleApiError } from "../utils/apiErrorHandler";

interface Official {
  _id: string;
  firstname: string;
  surname: string;
  portrait?: string;
}

const OfficialsManagementPage: React.FC = () => {
  const [officials, setOfficials] = useState<Official[]>([]);
  const [newOfficial, setNewOfficial] = useState<Official>({
    _id: "",
    firstname: "",
    surname: "",
    portrait: "",
  });
  const [editedRows, setEditedRows] = useState<{ [key: string]: boolean }>({});
  const apiRoute = "/api/officials";

  // 🔹 Offizielle laden
  useEffect(() => {
    fetchOfficials();
  }, []);

  const fetchOfficials = async () => {
    try {
      const response = await axios.get(apiRoute);
      setOfficials(response.data);
    } catch (error: any) {
      handleApiError(error, "Fehler beim Laden der Offiziellen.");
    }
  };

  // 🔹 Offiziellen hinzufügen
  const addOfficial = async () => {
    try {
      await axios.post(apiRoute, newOfficial);
      setNewOfficial({ _id: "", firstname: "", surname: "", portrait: "" });
      fetchOfficials();
    } catch (error: any) {
      handleApiError(error, "Fehler beim Hinzufügen eines Offiziellen.");
    }
  };

  // 🔹 Offiziellen löschen
  const deleteOfficial = async (officialId: string) => {
    try {
      await axios.delete(`${apiRoute}/${officialId}`);
      fetchOfficials();
    } catch (error: any) {
      handleApiError(error, "Fehler beim Löschen des Offiziellen.");
    }
  };

  // 🔹 Änderungen speichern
  const saveRow = async (official: Official) => {
    try {
      await axios.put(`${apiRoute}/${official._id}`, official);
      setEditedRows((prev) => ({ ...prev, [official._id]: false })); // Markierung entfernen
      fetchOfficials();
    } catch (error: any) {
      handleApiError(error, "Fehler beim Speichern der Änderung(en).");
    }
  };

  // 🔹 Spalten für die DataGrid-Tabelle
  const columns: GridColDef[] = [
    { field: "firstname", headerName: "Vorname", flex: 1, minWidth: 100, editable: true },
    { field: "surname", headerName: "Nachname", flex: 1, minWidth: 100, editable: true },
    {
      field: "actions",
      headerName: "Aktionen",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={() => saveRow(params.row)}
            disabled={!editedRows[params.row._id]}
          >
            Speichern
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => deleteOfficial(params.row._id)}
            sx={{ ml: 1 }}
          >
            Löschen
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Typography variant="h5" gutterBottom>
        Offiziellenverwaltung
      </Typography>

      {/* 🔹 Eingabefelder für neuen Offiziellen */}
      <Box display="flex" gap={2} sx={{ mb: 2 }}>
        <TextField
          label="Vorname"
          value={newOfficial.firstname}
          onChange={(e) => setNewOfficial({ ...newOfficial, firstname: e.target.value })}
        />
        <TextField
          label="Nachname"
          value={newOfficial.surname}
          onChange={(e) => setNewOfficial({ ...newOfficial, surname: e.target.value })}
        />
        <Button variant="contained" color="primary" onClick={addOfficial}>
          Hinzufügen
        </Button>
      </Box>

      {/* 🔹 MUI DataGrid für Offizielle */}
      <Box sx={{ height: "auto", minHeight: 400, width: "100%", mt: 2 }}>
        <DataGrid
          rows={officials}
          columns={columns}
          getRowId={(row) => row._id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          processRowUpdate={(updatedRow) => {
            setEditedRows((prev) => ({ ...prev, [updatedRow._id]: true })); // Zeile als geändert markieren
            return updatedRow;
          }}
          sx={{
            height: "auto",
            minHeight: 400, // Mindesthöhe für gute Lesbarkeit
            maxHeight: "70vh", // Verhindert, dass die Tabelle zu groß wird
            "& .MuiDataGrid-row": {
              transition: "background-color 0.3s",
            },
            "& .MuiDataGrid-row.edited": {
              backgroundColor: "#fff3cd", // Hellgelb für geänderte Zeilen
            },
          }}
          getRowClassName={(params) => (editedRows[params.row._id] ? "edited" : "")}
        />
      </Box>
    </>
  );
};

export default OfficialsManagementPage;